import { useQuery } from '@apollo/client'
import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material'
import {
    CandidateFilterCategory,
    SearchIndexField,
    type GetTitleOptionsQuery,
} from '__generated__/graphql'
import { useMemo, useState } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import { useRosterSearchFilter } from 'recruiter/hooks'
import { GetTitleOptions } from 'shared/graphql/titles.gql'

export function RosterTitleFilter() {
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterTitles
    )
    const [titleOptions, setTitleOptions] = useState<
        GetTitleOptionsQuery['titles']
    >([])
    const [open, setOpen] = useState(false)

    const { loading: areTitleOptionsLoading } = useQuery(GetTitleOptions, {
        onCompleted(data) {
            setTitleOptions(data?.titles ?? [])
        },
    })

    const titlesInFilter = useMemo(() => {
        const titleIdsInFilter = value?.value?.stringArray ?? []
        return titleOptions.filter(({ id }) => titleIdsInFilter.includes(id))
    }, [titleOptions, value])

    const handleTitleChange: AutocompleteProps<
        (typeof titleOptions)[number],
        true,
        false,
        false
    >['onChange'] = (_, newValues) => {
        if (newValues.length === 0) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { stringArray: newValues.map(({ id }) => id) },
        })
    }

    return (
        <FilterWrapper label="Title">
            <Autocomplete
                ChipProps={{ size: 'small' }}
                loading={areTitleOptionsLoading}
                multiple
                onChange={handleTitleChange}
                getOptionLabel={({ value }) => value}
                options={titleOptions}
                renderInput={(params) => (
                    <TextField {...params} placeholder="Search titles..." />
                )}
                value={titlesInFilter}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (reason === 'blur') {
                        setOpen(false)
                    }
                }}
            />
        </FilterWrapper>
    )
}
